import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import WebsiteTopBar from '../../components/WebsiteTopBar';
import CommonService from '../../services/common.service';
import {
  ADDITIONAL_SERVICE_OPTIONS,
  legacyPreferredPaymentTermsObjectToLabels,
} from './logisticsGenericRfqFormState';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/website-top-bar.scss';
import '../../assets/styles/logistics_rfq_submitted_view.scss';
import '../../assets/styles/logistics_provider_rfqs_received.scss';
import '../../assets/styles/business_relocation_rfq.scss';
import '../../assets/styles/business_relocation_rfq_submitted.scss';
import '../../assets/styles/exhibition_logistics_rfq.scss';
import '../../assets/styles/exhibition_logistics_rfq_submitted.scss';
import ExhibitionLogisticsRfqSubmittedView from './ExhibitionLogisticsRfqSubmittedView';
import BusinessRelocationRfqSubmittedView from './BusinessRelocationRfqSubmittedView';
import {
  LOGISTICS_LOCATION_FIELD_KEYS,
  resolveLogisticsLocationFields,
} from '../../utils/logisticsLocationDisplay';

/** Normalize API / payload RFQ kind (supports `rfqKind`, `rfq_type`, underscores). */
function normalizeSubmittedRfqKind(raw) {
  if (raw == null || String(raw).trim() === '') return 'generic';
  const s = String(raw).trim().toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-');
  if (s === 'exhibition-logistics') return 'exhibition-logistics';
  if (s === 'business-relocation') return 'business-relocation';
  if (s === 'generic') return 'generic';
  return s;
}

/** Parse `companyId` query (e.g. `9-` → 9). */
function parseCompanyIdParam(raw) {
  if (raw == null || String(raw).trim() === '') return null;
  const m = String(raw).trim().match(/^(\d+)/);
  const n = m ? parseInt(m[1], 10) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
}

function formatLogisticsProviderCompanyDisplay(_companyId, companyName) {
  const name =
    companyName != null && String(companyName).trim() !== '' ? String(companyName).trim() : null;
  return name;
}

/** Resolve `from` for back navigation: query → location state → referrer path (last resort). */
function resolveLogisticsViewBackFrom(searchParams, locationState) {
  const q = searchParams.get('from');
  if (q != null && String(q).trim() !== '') return String(q).trim().toLowerCase();
  if (locationState && typeof locationState === 'object' && locationState.from != null) {
    return String(locationState.from).trim().toLowerCase();
  }
  if (typeof document !== 'undefined' && document.referrer) {
    try {
      const u = new URL(document.referrer);
      const path = u.pathname.replace(/\/$/, '') || '/';
      if (path === '/logistics-rfqs-sent' || path.endsWith('/logistics-rfqs-sent')) return 'sent';
      if (path === '/dashboard/logistics/rfqs' || path.endsWith('/dashboard/logistics/rfqs')) return 'logistics-inbox';
      if (path === '/dashboard/logistics/rfqs/view' || path.endsWith('/dashboard/logistics/rfqs/view')) {
        return 'logistics-inbox';
      }
      if (path === '/logistics-providers' || path.endsWith('/logistics-providers')) return 'providers';
    } catch {
      /* ignore */
    }
  }
  return null;
}

function humanizeKey(k) {
  return String(k)
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

const GENERIC_LABELS = {
  companyId: 'Target provider company ID',
  rfqReferenceNumber: 'RFQ reference',
  rfqDate: 'RFQ date',
  rfqStatus: 'Status / priority',
  companyName: 'Company name',
  industry: 'Industry',
  contactPerson: 'Contact person',
  contactNumber: 'Phone',
  email: 'Email',
  companyAddress: 'Company address',
  companyWebsite: 'Website',
  transportMode: 'Transport mode',
  modeDetail: 'Shipment type',
  commodityType: 'Commodity type',
  hsCode: 'HS code',
  originCountry: 'Origin country',
  originState: 'Origin state',
  originCity: 'Origin city',
  originAddress: 'Origin address',
  originZip: 'Origin ZIP / postal code',
  originContactName: 'Origin contact name',
  originContactNumber: 'Origin contact phone',
  destCountry: 'Destination country',
  destState: 'Destination state',
  destCity: 'Destination city',
  destAddress: 'Destination address',
  destZip: 'Destination ZIP / postal code',
  destContactName: 'Destination contact name',
  destContactNumber: 'Destination contact phone',
  cargoDescription: 'Cargo description',
  totalUnits: 'Total units',
  totalWeightValue: 'Total weight',
  totalWeightUnit: 'Total weight unit',
  totalVolumeValue: 'Total volume',
  totalVolumeUnit: 'Total volume unit',
  cargoValue: 'Cargo value',
  cargoValueCurrency: 'Cargo value currency',
  unitWeightValue: 'Unit weight',
  unitWeightUnit: 'Unit weight unit',
  charGeneralCargo: 'General cargo',
  charOversized: 'Oversized',
  charPerishable: 'Perishable',
  charHeavyMachinery: 'Heavy machinery',
  charFragile: 'Fragile',
  charValuableGoods: 'Valuable goods',
  charHazardous: 'Hazardous',
  charDocuments: 'Documents',
  cargoTemperatureControlled: 'Temperature controlled',
  specialHazardousMaterial: 'Special hazardous material',
  specialStackable: 'Stackable',
  fragileHandling: 'Fragile handling',
  cargoHazardous: 'Cargo hazardous',
  cargoFragile: 'Cargo fragile',
  cargoPerishable: 'Cargo perishable',
  requiresLiftgate: 'Requires liftgate',
  standardDelivery: 'Standard delivery',
  oversized: 'Oversized shipment',
  pickupDate: 'Pickup date',
  deliveryDate: 'Expected delivery date',
  pickupTimeFrom: 'Pickup time (from)',
  pickupTimeTo: 'Pickup time (to)',
  deliveryTimeFrom: 'Delivery time (from)',
  deliveryTimeTo: 'Delivery time (to)',
  pickupTimeWindow: 'Pickup time window',
  deliveryTimeWindow: 'Delivery time window',
  urgencyLevel: 'Urgency',
  flexibleDates: 'Flexible dates',
  cargoInsuranceRequired: 'Cargo insurance required',
  customsClearanceSupport: 'Customs clearance support',
  customsBrokerNeeded: 'Customs broker needed',
  loadingEquipmentRequired: 'Loading equipment required',
  unloadingEquipmentRequired: 'Unloading equipment required',
  appointmentDockRequired: 'Appointment / dock required',
  professionalPackagingRequired: 'Professional packaging required',
  cargoPalletized: 'Cargo palletized',
  incoterm: 'Incoterm',
  expectedBudgetAmount: 'Expected budget',
  expectedBudgetCurrency: 'Currency (budget / commercial)',
  quoteDeadline: 'Quote deadline',
  quoteValidityPeriod: 'Quote validity period',
  creditTerms: 'Credit terms',
  preferredPaymentTerms: 'Preferred payment terms',
  shipmentFrequency: 'Shipment frequency',
  expectedVolumePerMonth: 'Expected volume / month',
  annualVolume: 'Annual volume',
  contractDuration: 'Contract duration',
  preferredCarriers: 'Preferred carriers',
  excludedCarriers: 'Excluded carriers',
  carrierRequirements: 'Carrier requirements',
  docCommercialInvoice: 'Document: commercial invoice',
  docPackingList: 'Document: packing list',
  docMsds: 'Document: MSDS',
  specialInstructionsComments: 'Special instructions',
  complianceRequirements: 'Compliance requirements',
  sustainabilityPreferences: 'Sustainability preferences',
  returnLogisticsRequired: 'Return logistics required',
  termsAccepted: 'Terms & conditions accepted',
  submittedAt: 'Submitted at (payload)',
  rfqKind: 'RFQ kind',
};

function labelFor(k) {
  return GENERIC_LABELS[k] || humanizeKey(k);
}

/** Renders any JSON-safe value for read-only display. */
function ValueDisplay({ value }) {
  if (value === null || value === undefined) {
    return <span className="lrfv-dash">—</span>;
  }
  if (typeof value === 'boolean') {
    return <span>{value ? 'Yes' : 'No'}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="lrfv-dash">—</span>;
    if (typeof value[0] === 'object' && value[0] !== null) {
      return (
        <pre className="lrfv-pre lrfv-pre--inline">{JSON.stringify(value, null, 2)}</pre>
      );
    }
    return <span>{value.map((x) => String(x)).join(', ')}</span>;
  }
  if (typeof value === 'object') {
    return (
      <pre className="lrfv-pre lrfv-pre--inline">{JSON.stringify(value, null, 2)}</pre>
    );
  }
  const s = String(value).trim();
  if (s === '') return <span className="lrfv-dash">—</span>;
  return <span>{s}</span>;
}

function DataRow({ label, value }) {
  return (
    <div className="lrfv-dl-row">
      <dt className="lrfv-dt">{label}</dt>
      <dd className="lrfv-dd">
        <ValueDisplay value={value} />
      </dd>
    </div>
  );
}

/** Section index 1–10: matches reference RFQ layout (icons + tones in SCSS). */
const SECTION_LAYOUT = [
  { tone: 'company', icon: '\u{1F464}' },
  { tone: 'freight', icon: '\u{1F6A2}' },
  { tone: 'pickup', icon: '\u{1F4CD}' },
  { tone: 'dest', icon: '\u{1F4CD}' },
  { tone: 'cargo', icon: '\u{1F4E6}' },
  { tone: 'timeline', icon: '\u{1F550}' },
  { tone: 'services', icon: '\u{2699}' },
  { tone: 'commercial', icon: '\u{1F4B2}' },
  { tone: 'docs', icon: '\u{1F4C4}' },
  { tone: 'special', icon: '\u{1F4AC}' },
];

function SectionCard({ sectionIndex, title, subtitle, children, plain }) {
  if (plain) {
    return (
      <section className="lrfv-section-card lrfv-section-card--plain">
        <header className="lrfv-section-card-head">
          <div className="lrfv-section-card-titles">
            <h2 className="lrfv-section-card-title">{title}</h2>
            {subtitle ? <p className="lrfv-section-card-sub">{subtitle}</p> : null}
          </div>
        </header>
        <div className="lrfv-section-card-body">{children}</div>
      </section>
    );
  }
  const idx = Math.min(Math.max((sectionIndex || 1) - 1, 0), SECTION_LAYOUT.length - 1);
  const { tone, icon } = SECTION_LAYOUT[idx];
  return (
    <section className={`lrfv-section-card lrfv-section-card--tone-${tone}`}>
      <header className="lrfv-section-card-head lrfv-section-card-head--with-icon">
        <span className={`lrfv-section-icon lrfv-section-icon--${tone}`} aria-hidden="true">
          {icon}
        </span>
        <div className="lrfv-section-card-titles">
          <h2 className="lrfv-section-card-title">{title}</h2>
          {subtitle ? <p className="lrfv-section-card-sub">{subtitle}</p> : null}
        </div>
      </header>
      <div className="lrfv-section-card-body">{children}</div>
    </section>
  );
}

/** Aligns with `SHIPMENT_TYPES_BY_TRANSPORT_MODE` keys (ocean = sea freight). */
const TRANSPORT_MODE_CHIPS = [
  { key: 'ocean', label: 'Sea' },
  { key: 'road', label: 'Road' },
  { key: 'rail', label: 'Rail' },
  { key: 'air', label: 'Air' },
  { key: 'multimodal', label: 'Multimodal' },
];

function TransportModeReadOnly({ value }) {
  const raw = value != null ? String(value).trim() : '';
  if (!raw) return <span className="lrfv-dash">—</span>;
  const norm = raw.toLowerCase();
  return (
    <div className="lrfv-mode-strip" role="group" aria-label="Mode of transport">
      {TRANSPORT_MODE_CHIPS.map(({ key, label }) => {
        const active = norm === key || (key === 'ocean' && norm === 'sea');
        return (
          <span
            key={key}
            className={`lrfv-mode-chip ${active ? 'lrfv-mode-chip--active' : 'lrfv-mode-chip--inactive'}`}
          >
            {label}
          </span>
        );
      })}
      {!TRANSPORT_MODE_CHIPS.some(
        ({ key }) => norm === key || (key === 'ocean' && norm === 'sea'),
      ) ? (
        <span className="lrfv-mode-custom">
          <strong className="lrfv-mode-custom-label">Selected</strong> {raw}
        </span>
      ) : null}
    </div>
  );
}

function UrgencyReadOnly({ value }) {
  const v = value != null ? String(value).trim() : '';
  if (!v) return <span className="lrfv-dash">—</span>;
  const norm = v.toLowerCase();
  const tier =
    norm.includes('urgent') || norm === 'urgent'
      ? 'urgent'
      : norm.includes('express')
        ? 'express'
        : 'standard';
  const hint =
    tier === 'urgent' ? '(fastest)' : tier === 'express' ? '(expedited)' : '(typical lead time)';
  return (
    <div className="lrfv-urgency-row">
      <span className={`lrfv-urgency-pill lrfv-urgency-pill--${tier}`}>{v}</span>
      <span className="lrfv-urgency-hint">{hint}</span>
    </div>
  );
}

/** Keys per section for “other fields” detection (flat). Incoterm lives in freight only. */
const GENERIC_SECTION_KEY_LISTS = {
  1: [
    'companyId',
    'rfqReferenceNumber',
    'rfqDate',
    'rfqStatus',
    'expectedBudgetCurrency',
    'companyName',
    'industry',
    'contactPerson',
    'contactNumber',
    'email',
    'companyAddress',
    'companyWebsite',
  ],
  2: ['transportMode', 'modeDetail', 'commodityType', 'hsCode', 'incoterm'],
  3: [
    'originCountry',
    'originState',
    'originCity',
    'originAddress',
    'originZip',
    'originContactName',
    'originContactNumber',
  ],
  4: [
    'destCountry',
    'destState',
    'destCity',
    'destAddress',
    'destZip',
    'destContactName',
    'destContactNumber',
  ],
  5: [
    'cargoDescription',
    'totalUnits',
    'totalWeightValue',
    'totalWeightUnit',
    'totalVolumeValue',
    'totalVolumeUnit',
    'cargoValue',
    'cargoValueCurrency',
    'unitWeightValue',
    'unitWeightUnit',
    'oversized',
    'standardDelivery',
    'charGeneralCargo',
    'charOversized',
    'charPerishable',
    'charHeavyMachinery',
    'charFragile',
    'charValuableGoods',
    'charHazardous',
    'charDocuments',
    'cargoTemperatureControlled',
    'specialHazardousMaterial',
    'specialStackable',
    'fragileHandling',
    'cargoHazardous',
    'cargoFragile',
    'cargoPerishable',
    'requiresLiftgate',
  ],
  6: [
    'pickupDate',
    'deliveryDate',
    'pickupTimeFrom',
    'pickupTimeTo',
    'deliveryTimeFrom',
    'deliveryTimeTo',
    'pickupTimeWindow',
    'deliveryTimeWindow',
    'urgencyLevel',
    'flexibleDates',
  ],
  7: [
    'cargoInsuranceRequired',
    'customsClearanceSupport',
    'customsBrokerNeeded',
    'loadingEquipmentRequired',
    'unloadingEquipmentRequired',
    'appointmentDockRequired',
    'professionalPackagingRequired',
    'cargoPalletized',
  ],
  8: [
    'expectedBudgetAmount',
    'quoteDeadline',
    'quoteValidityPeriod',
    'creditTerms',
    'shipmentFrequency',
    'expectedVolumePerMonth',
    'annualVolume',
    'contractDuration',
  ],
  9: ['preferredCarriers', 'excludedCarriers', 'carrierRequirements', 'docCommercialInvoice', 'docPackingList', 'docMsds'],
  10: [
    'specialInstructionsComments',
    'complianceRequirements',
    'sustainabilityPreferences',
    'returnLogisticsRequired',
    'termsAccepted',
    'rfqKind',
    'submittedAt',
  ],
};

const CARGO_SECTION_MAIN_KEYS = [
  'cargoDescription',
  'totalUnits',
  'totalWeightValue',
  'totalWeightUnit',
  'totalVolumeValue',
  'totalVolumeUnit',
  'cargoValue',
  'cargoValueCurrency',
  'unitWeightValue',
  'unitWeightUnit',
  'oversized',
  'standardDelivery',
];

const CARGO_SECTION_CLASS_KEYS = [
  'charGeneralCargo',
  'charOversized',
  'charPerishable',
  'charHeavyMachinery',
  'charFragile',
  'charValuableGoods',
  'charHazardous',
  'charDocuments',
];

const CARGO_SECTION_SPECIAL_KEYS = [
  'cargoTemperatureControlled',
  'specialHazardousMaterial',
  'specialStackable',
  'fragileHandling',
  'cargoHazardous',
  'cargoFragile',
  'cargoPerishable',
  'requiresLiftgate',
];

function PackagesTableInner({ packages }) {
  if (!Array.isArray(packages) || packages.length === 0) {
    return <div className="lrfv-muted lrfv-subsection-pad">No package lines.</div>;
  }
  return (
    <div className="lrfv-table-scroll">
      <table className="lrfv-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Package ID</th>
            <th>Length</th>
            <th>Width</th>
            <th>Height</th>
            <th>Weight (kg)</th>
            <th>Qty</th>
          </tr>
        </thead>
        <tbody>
          {packages.map((p, i) => (
            <tr key={p.id || i}>
              <td>{i + 1}</td>
              <td>{p.packageNumber != null && p.packageNumber !== '' ? p.packageNumber : '—'}</td>
              <td>{p.length != null && p.length !== '' ? p.length : '—'}</td>
              <td>{p.width != null && p.width !== '' ? p.width : '—'}</td>
              <td>{p.height != null && p.height !== '' ? p.height : '—'}</td>
              <td>{p.weightKg != null && p.weightKg !== '' ? p.weightKg : '—'}</td>
              <td>{p.quantity != null && p.quantity !== '' ? p.quantity : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DocumentsInner({ documents }) {
  if (!Array.isArray(documents) || documents.length === 0) {
    return <div className="lrfv-muted">No supporting documents listed.</div>;
  }
  return (
    <ul className="lrfv-doc-list">
      {documents.map((d, i) => (
        <li key={d.id || i} className="lrfv-doc-item">
          <span className="lrfv-doc-name">{d.fileName || 'Untitled file'}</span>
          {d.sizeLabel ? <span className="lrfv-doc-meta">{d.sizeLabel}</span> : null}
          {d.hasFile != null || d.file != null ? (
            <span className="lrfv-doc-meta">{d.hasFile || d.file ? 'File attached' : 'No file'}</span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

const ADDITIONAL_SERVICE_LABEL_BY_KEY = Object.fromEntries(
  ADDITIONAL_SERVICE_OPTIONS.map((o) => [o.key, o.label]),
);

function AdditionalServicesInner({ additionalServices }) {
  if (!additionalServices || typeof additionalServices !== 'object' || Array.isArray(additionalServices)) {
    return <div className="lrfv-muted">None selected.</div>;
  }
  const selected = Object.entries(additionalServices).filter(([, v]) => Boolean(v));
  if (selected.length === 0) return <div className="lrfv-muted">None selected.</div>;
  return (
    <ul className="lrfv-check-list lrfv-check-list--checked">
      {selected.map(([k]) => (
        <li key={k}>
          <span className="lrfv-check-mark" aria-hidden="true">
            ✓
          </span>
          {ADDITIONAL_SERVICE_LABEL_BY_KEY[k] || labelFor(k)}
        </li>
      ))}
    </ul>
  );
}

function normalizePreferredPaymentTerms(terms) {
  if (Array.isArray(terms)) return terms.filter(Boolean);
  if (terms && typeof terms === 'object') return legacyPreferredPaymentTermsObjectToLabels(terms);
  return [];
}

function PaymentTermsInner({ terms }) {
  const list = normalizePreferredPaymentTerms(terms);
  if (list.length === 0) return <div className="lrfv-muted">None specified.</div>;
  return (
    <ul className="lrfv-check-list lrfv-check-list--checked">
      {list.map((t, i) => (
        <li key={i}>
          <span className="lrfv-check-mark" aria-hidden="true">
            ✓
          </span>
          {String(t)}
        </li>
      ))}
    </ul>
  );
}

const RESERVED_PAYLOAD_KEYS = new Set([
  'packages',
  'documents',
  'additionalServices',
  'preferredPaymentTerms',
]);

const SECTION_KEY_SET = (() => {
  const s = new Set();
  Object.values(GENERIC_SECTION_KEY_LISTS).forEach((keys) => keys.forEach((k) => s.add(k)));
  return s;
})();

function renderDlRows(keys, payload, displayOverrides = {}) {
  return keys.map((k) => {
    const raw = payload[k];
    const value =
      Object.prototype.hasOwnProperty.call(displayOverrides, k) && displayOverrides[k] != null
        ? displayOverrides[k]
        : raw;
    return <DataRow key={k} label={labelFor(k)} value={value} />;
  });
}

function GenericPayloadView({ payload }) {
  const [locationLabels, setLocationLabels] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const resolved = await resolveLogisticsLocationFields(payload);
      if (!cancelled) setLocationLabels(resolved);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    payload.originCountry,
    payload.originState,
    payload.originCity,
    payload.destCountry,
    payload.destState,
    payload.destCity,
  ]);

  const locationDisplay = useMemo(() => {
    const out = {};
    LOGISTICS_LOCATION_FIELD_KEYS.forEach((k) => {
      if (locationLabels[k] != null && String(locationLabels[k]).trim() !== '') {
        out[k] = locationLabels[k];
      }
    });
    return out;
  }, [locationLabels]);
  const otherKeys = useMemo(
    () =>
      Object.keys(payload)
        .filter((k) => !SECTION_KEY_SET.has(k) && !RESERVED_PAYLOAD_KEYS.has(k))
        .sort(),
    [payload],
  );

  const s2 = GENERIC_SECTION_KEY_LISTS[2].filter((k) => k !== 'transportMode');
  const s6 = GENERIC_SECTION_KEY_LISTS[6].filter((k) => k !== 'urgencyLevel');

  return (
    <div className="lrfv-generic">
      <SectionCard
        sectionIndex={1}
        title="Bill to & company information"
        subtitle="RFQ reference, currency, and company / contact details"
      >
        <dl className="lrfv-dl">{renderDlRows(GENERIC_SECTION_KEY_LISTS[1], payload)}</dl>
      </SectionCard>

      <SectionCard
        sectionIndex={2}
        title="International freight forwarding"
        subtitle="Transport mode, shipment type, commodity, HS code, and Incoterm"
      >
        <dl className="lrfv-dl">
          <div className="lrfv-dl-row">
            <dt className="lrfv-dt">Mode of transport</dt>
            <dd className="lrfv-dd">
              <TransportModeReadOnly value={payload.transportMode} />
            </dd>
          </div>
          {renderDlRows(s2, payload)}
        </dl>
      </SectionCard>

      <SectionCard sectionIndex={3} title="Shipping — pickup location" subtitle="Origin address and contact">
        <dl className="lrfv-dl">{renderDlRows(GENERIC_SECTION_KEY_LISTS[3], payload, locationDisplay)}</dl>
      </SectionCard>

      <SectionCard sectionIndex={4} title="Destination — delivery location" subtitle="Destination address and contact">
        <dl className="lrfv-dl">{renderDlRows(GENERIC_SECTION_KEY_LISTS[4], payload, locationDisplay)}</dl>
      </SectionCard>

      <SectionCard
        sectionIndex={5}
        title="Cargo details & specifications"
        subtitle="Description, metrics, classification, handling flags, and package dimensions"
      >
        <dl className="lrfv-dl">{renderDlRows(CARGO_SECTION_MAIN_KEYS, payload)}</dl>
        <div className="lrfv-subsection">
          <h3 className="lrfv-subsection-title">Packing dimensions</h3>
          <PackagesTableInner packages={payload.packages} />
        </div>
        <div className="lrfv-subsection">
          <h3 className="lrfv-subsection-title">Cargo classification</h3>
          <dl className="lrfv-dl">{renderDlRows(CARGO_SECTION_CLASS_KEYS, payload)}</dl>
        </div>
        <div className="lrfv-subsection">
          <h3 className="lrfv-subsection-title">Special handling & treatment</h3>
          <dl className="lrfv-dl">{renderDlRows(CARGO_SECTION_SPECIAL_KEYS, payload)}</dl>
        </div>
      </SectionCard>

      <SectionCard
        sectionIndex={6}
        title="Timeline & delivery level"
        subtitle="Pickup / delivery schedule, windows, and urgency"
      >
        <dl className="lrfv-dl">
          {renderDlRows(s6, payload)}
          <div className="lrfv-dl-row">
            <dt className="lrfv-dt">Shipment speed / urgency</dt>
            <dd className="lrfv-dd">
              <UrgencyReadOnly value={payload.urgencyLevel} />
            </dd>
          </div>
        </dl>
      </SectionCard>

      <SectionCard
        sectionIndex={7}
        title="Service requirements & additional services"
        subtitle="Primary service toggles and add-on services"
      >
        <dl className="lrfv-dl">{renderDlRows(GENERIC_SECTION_KEY_LISTS[7], payload)}</dl>
        <div className="lrfv-subsection">
          <h3 className="lrfv-subsection-title">Additional services</h3>
          <AdditionalServicesInner additionalServices={payload.additionalServices} />
        </div>
      </SectionCard>

      <SectionCard
        sectionIndex={8}
        title="Commercial terms & payment"
        subtitle="Budget, deadlines, volume, contract, and payment preferences"
      >
        <dl className="lrfv-dl">{renderDlRows(GENERIC_SECTION_KEY_LISTS[8], payload)}</dl>
        <div className="lrfv-subsection">
          <h3 className="lrfv-subsection-title">Preferred payment terms</h3>
          <PaymentTermsInner terms={payload.preferredPaymentTerms} />
        </div>
      </SectionCard>

      <SectionCard
        sectionIndex={9}
        title="Terms, conditions & documentation"
        subtitle="Required documents, carrier preferences, and uploads"
      >
        <dl className="lrfv-dl">{renderDlRows(GENERIC_SECTION_KEY_LISTS[9], payload)}</dl>
        <div className="lrfv-subsection">
          <h3 className="lrfv-subsection-title">Supporting uploads</h3>
          <DocumentsInner documents={payload.documents} />
        </div>
      </SectionCard>

      <SectionCard
        sectionIndex={10}
        title="Special instructions & additional parameters"
        subtitle="Comments, compliance, sustainability, and acknowledgements"
      >
        <dl className="lrfv-dl">{renderDlRows(GENERIC_SECTION_KEY_LISTS[10], payload)}</dl>
      </SectionCard>

      {otherKeys.length > 0 ? (
        <SectionCard
          plain
          title="Other fields"
          subtitle="Extra keys on this submission (e.g. migrated or future form versions)"
        >
          <dl className="lrfv-dl">
            {otherKeys.map((k) => (
              <DataRow key={k} label={labelFor(k)} value={payload[k]} />
            ))}
          </dl>
        </SectionCard>
      ) : null}
    </div>
  );
}

function DedicatedSubmissionMeta({ referenceDisplay, submittedDisplay, data, providerCompanyDisplay }) {
  return (
    <div className="lrfv-meta-grid lrfv-meta-grid--in-hero">
      {referenceDisplay ? (
        <div className="lrfv-meta-card">
          <span className="lrfv-meta-label">Reference</span>
          <span className="lrfv-meta-value lrfv-meta-value--mono">{referenceDisplay}</span>
        </div>
      ) : null}
      {submittedDisplay ? (
        <div className="lrfv-meta-card">
          <span className="lrfv-meta-label">Submitted on</span>
          <span className="lrfv-meta-value">{new Date(submittedDisplay).toLocaleString()}</span>
        </div>
      ) : null}
      {data?.allocator ? (
        <div className="lrfv-meta-card">
          <span className="lrfv-meta-label">Contact</span>
          <span className="lrfv-meta-value">
            {data.allocator.name}
            {data.allocator.roleLabel ? ` (${data.allocator.roleLabel})` : ''}
          </span>
        </div>
      ) : null}
      {providerCompanyDisplay ? (
        <div className="lrfv-meta-card">
          <span className="lrfv-meta-label">Logistics provider</span>
          <span className="lrfv-meta-value">{providerCompanyDisplay}</span>
        </div>
      ) : null}
    </div>
  );
}

export default function LogisticsRfqSubmittedViewPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const ref = searchParams.get('ref') || '';
  const rfqKindQuery = searchParams.get('kind') || searchParams.get('rfqKind') || '';
  const companyIdRaw = searchParams.get('companyId');
  const companyIdDisplay = parseCompanyIdParam(companyIdRaw);

  const backFromResolved = resolveLogisticsViewBackFrom(searchParams, location.state);
  const isDashboardShell =
    location.pathname.includes('/dashboard/logistics/rfqs/view') ||
    backFromResolved === 'logistics-inbox';
  let backHref = '/logistics-providers';
  let backLabel = '← Back to Logistics Providers';
  if (backFromResolved === 'sent') {
    backHref = '/logistics-rfqs-sent';
    backLabel = '← Back to Sent RFQs';
  } else if (backFromResolved === 'logistics-inbox' || isDashboardShell) {
    backHref = '/dashboard/logistics/rfqs';
    backLabel = '← Back to received RFQs';
  }

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [providerCompanyNameFallback, setProviderCompanyNameFallback] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setError(null);
      setData(null);
      const numId = id != null ? parseInt(String(id), 10) : NaN;
      if (!Number.isFinite(numId) || numId <= 0) {
        setLoading(false);
        setError('Invalid link. Open View RFQ from the logistics providers list after submitting an RFQ.');
        return;
      }
      setLoading(true);
      try {
        const query = { ref: ref.trim() };
        const kindHint = rfqKindQuery != null ? String(rfqKindQuery).trim() : '';
        if (kindHint === 'business-relocation' || kindHint === 'exhibition-logistics') {
          query.kind = kindHint;
        }
        const res = await CommonService.get(`/company/logistics-providers/rfq/submission/${numId}`, query);
        if (cancelled) return;
        const api = res ?? {};
        const row = api.data ?? api;
        const ok =
          api.success !== false &&
          row &&
          typeof row === 'object' &&
          (row.id != null || row.payload != null);
        if (ok) {
          setData(row);
        } else {
          setError(api.message || 'Could not load submission.');
        }
      } catch (e) {
        if (cancelled) return;
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Could not load submission.';
        setError(typeof msg === 'string' ? msg : 'Could not load submission.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [id, ref, rfqKindQuery]);

  useEffect(() => {
    if (!isDashboardShell) {
      setProviderCompanyNameFallback(null);
      return undefined;
    }
    const apiName = data?.logisticsProviderCompanyName ?? data?.logistics_provider_company_name;
    if (apiName != null && String(apiName).trim() !== '') {
      setProviderCompanyNameFallback(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await CommonService.get('/company/my-profile');
        const body = res?.data ?? res ?? {};
        const profile = body?.data ?? body;
        const bi =
          profile?.basic_information && typeof profile.basic_information === 'object'
            ? profile.basic_information
            : profile?.basicInformation && typeof profile.basicInformation === 'object'
              ? profile.basicInformation
              : {};
        const name =
          profile?.company_name ||
          profile?.companyName ||
          bi?.companyName ||
          bi?.company_name ||
          null;
        if (!cancelled && name != null && String(name).trim() !== '') {
          setProviderCompanyNameFallback(String(name).trim());
        }
      } catch {
        if (!cancelled) setProviderCompanyNameFallback(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isDashboardShell, data]);

  const kindHint = normalizeSubmittedRfqKind(rfqKindQuery);
  const kind = normalizeSubmittedRfqKind(
    data?.rfqKind ||
      data?.rfq_type ||
      data?.payload?.rfqKind ||
      data?.payload?.rfq_type ||
      kindHint,
  );
  const isDedicatedKind = kind === 'business-relocation' || kind === 'exhibition-logistics';
  const payload = data?.payload && typeof data.payload === 'object' ? data.payload : {};
  const enrichedPayload = useMemo(() => {
    const base = { ...payload };
    const ref = data?.rfqReferenceNumber || base.rfqReferenceNumber || base.rfqReference || '';
    const submitted = data?.submittedAt || base.submittedAt || base.submittedDate || base.createdAt || null;
    const status = data?.status || base.rfqStatus || base.status || null;
    if (ref) {
      base.rfqReference = ref;
      base.rfqReferenceNumber = ref;
    }
    if (status != null && String(status).trim() !== '') base.status = status;
    if (submitted) {
      base.createdAt = submitted;
      base.submittedDate = submitted;
    }
    return base;
  }, [data, payload]);
  const referenceDisplay = data?.rfqReferenceNumber || enrichedPayload?.rfqReferenceNumber || '';
  const submittedDisplay = data?.submittedAt || enrichedPayload?.submittedDate || null;
  const providerCompanyDisplay = formatLogisticsProviderCompanyDisplay(
    data?.logisticsProviderCompanyId ?? data?.logistics_provider_company_id ?? companyIdDisplay,
    data?.logisticsProviderCompanyName ??
      data?.logistics_provider_company_name ??
      providerCompanyNameFallback,
  );
  const statusLabel = data?.status || enrichedPayload?.status || payload?.rfqStatus || '—';
  const kindLabel =
    kind === 'business-relocation'
      ? 'Business relocation'
      : kind === 'exhibition-logistics'
        ? 'Exhibition logistics'
        : 'Generic logistics';
  const dashboardBreadcrumbCurrent =
    kind === 'business-relocation'
      ? 'Business Relocation Quote'
      : kind === 'exhibition-logistics'
        ? 'Exhibition Logistics Quote'
        : 'RFQ details';

  return (
    <div
      className={`lrfv-page${isDedicatedKind ? ' lrfv-page--dedicated' : ''}${
        isDashboardShell ? ' lrfv-page--dashboard' : ''
      }`}
    >
      {!isDashboardShell ? <WebsiteTopBar /> : null}
      <main className={`lrfv-main${isDedicatedKind ? ' lrfv-main--wide' : ''}`}>
        <div className="lrfv-shell">
          {isDashboardShell ? (
            <div className="lrfv-dashboard-topbar">
              <nav aria-label="breadcrumb" className="lprfq-breadcrumb lrfv-dashboard-breadcrumb">
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to="/dashboard/logistics">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/dashboard/logistics/rfqs">RFQs Received</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {dashboardBreadcrumbCurrent}
                  </li>
                </ol>
              </nav>
              <Link to={backHref} className="lrfv-back lrfv-back--dashboard">
                {backLabel}
              </Link>
            </div>
          ) : (
            <nav className="lrfv-nav">
              <Link to={backHref} className="lrfv-back">
                {backLabel}
              </Link>
            </nav>
          )}

          <header className={`lrfv-hero${isDedicatedKind ? ' lrfv-hero--dedicated' : ''}`}>
            <div className="lrfv-hero-top">
              <div className="lrfv-hero-text">
                <p className="lrfv-kicker">Read-only submission</p>
                <h1 className="lrfv-title">
                  {kind === 'exhibition-logistics'
                    ? 'Exhibition Logistics Quote'
                    : kind === 'business-relocation'
                      ? 'Business Relocation Quote'
                      : 'Logistics RFQ details'}
                </h1>
                <p className="lrfv-lead">
                  {kind === 'exhibition-logistics'
                    ? 'Review your exhibition logistics request. This page is not editable.'
                    : kind === 'business-relocation'
                      ? 'Review your business relocation request. This page is not editable.'
                      : 'Review everything you submitted. This page is not editable.'}
                </p>
              </div>
              <div className="lrfv-hero-meta">
                <span className="lrfv-pill lrfv-pill--kind">{kindLabel}</span>
                <span className="lrfv-pill lrfv-pill--status">Status: {statusLabel}</span>
              </div>
            </div>
            {isDedicatedKind && !loading && !error && data ? (
              <DedicatedSubmissionMeta
                referenceDisplay={referenceDisplay}
                submittedDisplay={submittedDisplay}
                data={data}
                providerCompanyDisplay={providerCompanyDisplay}
              />
            ) : null}
          </header>

          {!isDedicatedKind ? (
            <div className="lrfv-meta-grid">
              {data?.allocator ? (
                <div className="lrfv-meta-card">
                  <span className="lrfv-meta-label">Contact</span>
                  <span className="lrfv-meta-value">
                    {data.allocator.name}
                    {data.allocator.roleLabel ? ` (${data.allocator.roleLabel})` : ''}
                  </span>
                </div>
              ) : null}
              {referenceDisplay ? (
                <div className="lrfv-meta-card">
                  <span className="lrfv-meta-label">Reference</span>
                  <span className="lrfv-meta-value lrfv-meta-value--mono">{referenceDisplay}</span>
                </div>
              ) : null}
              {submittedDisplay ? (
                <div className="lrfv-meta-card">
                  <span className="lrfv-meta-label">Submitted</span>
                  <span className="lrfv-meta-value">
                    {new Date(submittedDisplay).toLocaleString()}
                  </span>
                </div>
              ) : null}
              {providerCompanyDisplay ? (
                <div className="lrfv-meta-card">
                  <span className="lrfv-meta-label">Logistics provider</span>
                  <span className="lrfv-meta-value">{providerCompanyDisplay}</span>
                </div>
              ) : null}
              {data?.id != null ? (
                <div className="lrfv-meta-card">
                  <span className="lrfv-meta-label">Submission ID</span>
                  <span className="lrfv-meta-value lrfv-meta-value--mono">#{data.id}</span>
                </div>
              ) : null}
            </div>
          ) : null}

          {loading ? (
            <div className="lrfv-status" role="status">
              Loading submission…
            </div>
          ) : null}
          {!loading && error ? (
            <div className="lrfv-error" role="alert">
              {error}
            </div>
          ) : null}
          {!loading && !error && data ? (
            <div className={`lrfv-body${isDedicatedKind ? ' lrfv-body--dedicated' : ''}`}>
              {kind === 'business-relocation' ? (
                <BusinessRelocationRfqSubmittedView
                  payload={enrichedPayload}
                  embedded
                  hideSummary
                  showOverviewHeading
                />
              ) : null}
              {kind === 'exhibition-logistics' ? (
                <ExhibitionLogisticsRfqSubmittedView
                  payload={enrichedPayload}
                  embedded
                  hideSummary
                  allocator={data?.allocator}
                />
              ) : null}
              {kind !== 'business-relocation' && kind !== 'exhibition-logistics' ? (
                <GenericPayloadView payload={payload} />
              ) : null}
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
}
